import { buildBaseURL } from '@warp-drive/utilities';
import { query } from '@warp-drive/utilities/json-api';
import { CONTRACT_HEADER } from '../handlers/type-normalization-handler.ts';
import { buildTioAuthHeaders } from '../utils/auth-headers.ts';
import type { LpnDiscountLetterSchool } from '../data/resources/lpn-discount-letter-school.type.ts';

const RESOURCE_PATH = 'api/v2/lpn-discount-letters';

/** Where a discount-letter download started; recorded with the download for reporting. */
export type DiscountLetterSource = 'lpn_page' | 'tas_dashboard';

/** The stable values the picker needs from a materialized discount-letter school. */
export interface DiscountLetterSchool {
  id: string;
  schoolName: string;
}

/** PDF bytes plus the deterministic local filename used for the download. */
export interface DiscountLetterFile {
  blob: Blob;
  filename: string;
}

/**
 * Builds a fresh, document-cached query for the schools available to an employee's company.
 *
 * @param employeeId the current user's active employee whose company determines availability
 * @returns a reload request that materializes `lpn-discount-letter-school` records
 */
export function discountLetterSchoolsQuery(employeeId: string) {
  const request = query<LpnDiscountLetterSchool>(
    'lpn-discount-letter-school',
    { employee_id: employeeId },
    { resourcePath: `${RESOURCE_PATH}/schools` }
  );
  const headers = new Headers(request.headers);
  headers.set(CONTRACT_HEADER, '2');

  return {
    ...request,
    headers,
    cacheOptions: { reload: true },
  };
}

/**
 * Downloads the letter for one school and records the supplied reporting source.
 * PDF responses bypass `store.request`, whose cache pipeline expects structured data.
 *
 * @param request the employee, LPN page and reporting source sent to the API
 * @param options the calling app's API key, supplied explicitly because tio-data is owner-free
 * @returns the PDF bytes and a filename rebuilt from the selected school's name
 * @throws when the API refuses or fails the request
 */
export async function requestDiscountLetter(
  {
    employeeId,
    school,
    source,
  }: {
    employeeId: string;
    school: DiscountLetterSchool;
    source: DiscountLetterSource;
  },
  { apiKey }: { apiKey: string }
): Promise<DiscountLetterFile> {
  const response = await fetch(buildBaseURL({ resourcePath: RESOURCE_PATH }), {
    method: 'POST',
    headers: buildTioAuthHeaders({
      apiKey,
      contentType: 'application/json',
      accept: 'application/pdf',
    }),
    body: JSON.stringify({ employee_id: employeeId, lpn_page_id: school.id, source }),
  });
  if (!response.ok) {
    throw new Error(`Discount letter request failed (${response.status})`);
  }

  return {
    blob: await response.blob(),
    filename: `tuition-discount-letter-${slugify(school.schoolName)}.pdf`,
  };
}

function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
